/**
 * Simple theme management hook for playground
 * Provides theme switching functionality without full theme context
 */

import { useState, useEffect, useCallback } from 'react';

export type PlaygroundTheme = 'light' | 'dark' | 'auto';

export interface PlaygroundThemeState {
  currentTheme: PlaygroundTheme;
  effectiveTheme: 'light' | 'dark';
  setTheme: (theme: PlaygroundTheme) => void;
}

/**
 * Simple theme hook for playground components
 * Manages theme state and applies theme classes to document
 */
export const usePlaygroundTheme = (): PlaygroundThemeState => {
  // Initialize theme from localStorage or default to 'light'
  const [currentTheme, setCurrentTheme] = useState<PlaygroundTheme>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('playground-theme') as PlaygroundTheme;
      return saved || 'light';
    }
    return 'light';
  });

  // Determine effective theme (auto follows system preference)
  const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark'>('light');

  // Apply theme changes to document and localStorage
  const setTheme = useCallback((theme: PlaygroundTheme) => {
    setCurrentTheme(theme);
    
    // Save to localStorage
    localStorage.setItem('playground-theme', theme);
    
    // Apply theme to document
    const html = document.documentElement;
    
    if (theme === 'dark') {
      html.classList.add('dark');
      setEffectiveTheme('dark');
    } else if (theme === 'light') {
      html.classList.remove('dark');
      setEffectiveTheme('light');
    } else if (theme === 'auto') {
      // Follow system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        html.classList.add('dark');
        setEffectiveTheme('dark');
      } else {
        html.classList.remove('dark');
        setEffectiveTheme('light');
      }
    }
  }, []);

  // Listen for system theme changes when in auto mode
  useEffect(() => {
    if (currentTheme === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = (e: MediaQueryListEvent) => {
        const html = document.documentElement;
        if (e.matches) {
          html.classList.add('dark');
          setEffectiveTheme('dark');
        } else {
          html.classList.remove('dark');
          setEffectiveTheme('light');
        }
      };

      mediaQuery.addEventListener('change', handleChange);
      
      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    }
    
    // Return empty cleanup function when not in auto mode
    return () => {};
  }, [currentTheme]);

  // Apply initial theme on mount
  useEffect(() => {
    setTheme(currentTheme);
  }, [currentTheme, setTheme]); // Include dependencies

  return {
    currentTheme,
    effectiveTheme,
    setTheme
  };
};